// Canonical generated copy has no destination or image fields.
export const LP_SMB_HOME_NAV_ANCHORS = [
  "services", "about", "why-us", "proof", "process", "areas", "faq", "contact",
] as const;
export type LpSmbHomeNavAnchor = (typeof LP_SMB_HOME_NAV_ANCHORS)[number];

export interface LpSmbHomeContent {
  eyebrow: string;
  headline: string;
  subhead: string;
  primaryCtaText: string;
  primaryCtaSub: string;
  trustBar: { label: string; items: string[] };
  services: { heading: string; dek: string; items: { icon: string; title: string; body: string }[] };
  about: { heading: string; body: string; highlights: string[] };
  whyUs: { heading: string; items: { title: string; body: string }[] };
  proof: { heading: string; items: { quote: string; attribution: string }[] };
  process: { heading: string; items: { title: string; body: string }[] };
  serviceAreas: { heading: string; dek: string; areas: string[] };
  faq: { heading: string; items: { q: string; a: string }[] };
  contact: { heading: string; dek: string; ctaText: string; phone: string; email: string; address: string; hours: string };
  footer: { tagline: string; disclaimer: string };
  nav: { items: { label: string; anchor: LpSmbHomeNavAnchor }[] };
}

function object(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? value as Record<string, unknown> : {};
}

function text(value: unknown): string {
  return typeof value === "string" ? value.replace(/\u2014/g, "-").trim() : "";
}

function strings(value: unknown, cap: number): string[] {
  return (Array.isArray(value) ? value : []).map(text).filter(Boolean).slice(0, cap);
}

function rows<T>(value: unknown, cap: number, map: (row: Record<string, unknown>) => T): T[] {
  return (Array.isArray(value) ? value : []).slice(0, cap).map((row) => map(object(row)));
}

// Missing facts stay empty. Never fill cardinality from a template seed.
export function normalizeLpSmbHomeContent(raw: unknown): LpSmbHomeContent {
  const root = object(raw);
  const trust = object(root.trustBar);
  const services = object(root.services);
  const about = object(root.about);
  const why = object(root.whyUs);
  const proof = object(root.proof);
  const process = object(root.process);
  const areas = object(root.serviceAreas);
  const faq = object(root.faq);
  const contact = object(root.contact);
  const footer = object(root.footer);
  const nav = object(root.nav);
  const titleBody = (row: Record<string, unknown>) => ({ title: text(row.title), body: text(row.body) });
  const navItems: LpSmbHomeContent["nav"]["items"] = [];
  for (const rawItem of Array.isArray(nav.items) ? nav.items : []) {
    const item = object(rawItem);
    const anchor = LP_SMB_HOME_NAV_ANCHORS.find((allowed) => allowed === text(item.anchor));
    const label = text(item.label);
    if (anchor && label) navItems.push({ label, anchor });
    if (navItems.length === 6) break;
  }
  return {
    eyebrow: text(root.eyebrow),
    headline: text(root.headline),
    subhead: text(root.subhead),
    primaryCtaText: text(root.primaryCtaText),
    primaryCtaSub: text(root.primaryCtaSub),
    trustBar: { label: text(trust.label), items: strings(trust.items, 6) },
    services: {
      heading: text(services.heading), dek: text(services.dek),
      items: rows(services.items, 8, (row) => ({ icon: text(row.icon), ...titleBody(row) })),
    },
    about: { heading: text(about.heading), body: text(about.body), highlights: strings(about.highlights, 4) },
    whyUs: { heading: text(why.heading), items: rows(why.items, 6, titleBody) },
    proof: {
      heading: text(proof.heading),
      items: rows(proof.items, 6, (row) => ({ quote: text(row.quote), attribution: text(row.attribution) })),
    },
    process: { heading: text(process.heading), items: rows(process.items, 5, titleBody) },
    serviceAreas: { heading: text(areas.heading), dek: text(areas.dek), areas: strings(areas.areas, 20) },
    faq: { heading: text(faq.heading), items: rows(faq.items, 8, (row) => ({ q: text(row.q), a: text(row.a) })) },
    contact: {
      heading: text(contact.heading), dek: text(contact.dek), ctaText: text(contact.ctaText),
      phone: text(contact.phone), email: text(contact.email), address: text(contact.address), hours: text(contact.hours),
    },
    footer: { tagline: text(footer.tagline), disclaimer: text(footer.disclaimer) },
    nav: { items: navItems },
  };
}

export function isLpSmbHomeContentUsable(content: LpSmbHomeContent | null | undefined): boolean {
  return Boolean(
    content?.headline.trim() && content.subhead.trim() && content.primaryCtaText.trim()
    && content.services.items.filter((item) => item.icon.trim() && item.title.trim() && item.body.trim()).length >= 3
    && content.contact.ctaText.trim() && content.serviceAreas.areas.some((area) => area.trim()),
  );
}

/** Readiness uses the owning run's frozen choice, never today's brand setting. */
export function isLpSmbHomeRunReady(run: { status: unknown; generatedContent?: unknown }): boolean {
  const gc = object(run.generatedContent);
  const imagePlan = object(gc.smbHomeImages);
  if (Array.isArray(imagePlan.slots) && imagePlan.slots.some((slot: any) => slot.status !== "ready" || !slot.url)) return false;
  if (run.status !== "completed" || object(gc.studioDesign).rendererTemplateKey !== "smb-home" ||
    !isLpSmbHomeContentUsable(assembleLpSmbHomeContent(gc))) return false;
  const design = object(gc.studioBrandDesign);
  if (design.submissionState === "ambiguous") return false;
  if (design.status) return design.status === "ready";
  const input = object(gc.studioBrandDesignInput);
  const frozen = object(object(gc.studioCreation).intent);
  return input.designSystemId === null || frozen.brandDesignSystemId === null;
}

export const LP_SMB_HOME_BLOCK_KEYS = [
  "shHero", "shTrustBar", "shServices", "shAbout", "shWhyUs", "shProof",
  "shProcess", "shAreas", "shFaq", "shContact", "shFooter", "shNav",
] as const;
export type LpSmbHomeBlockKey = typeof LP_SMB_HOME_BLOCK_KEYS[number];
export const LP_SMB_HOME_SUBTREES = {
  shHero: ["eyebrow", "headline", "subhead", "primaryCtaText", "primaryCtaSub"],
  shTrustBar: ["trustBar"], shServices: ["services"], shAbout: ["about"],
  shWhyUs: ["whyUs"], shProof: ["proof"], shProcess: ["process"],
  shAreas: ["serviceAreas"], shFaq: ["faq"], shContact: ["contact"],
  shFooter: ["footer"], shNav: ["nav"],
} as const satisfies Record<LpSmbHomeBlockKey, readonly (keyof LpSmbHomeContent)[]>;
export type LpSmbHomeBlocks = {
  shHero: Pick<LpSmbHomeContent, "eyebrow" | "headline" | "subhead" | "primaryCtaText" | "primaryCtaSub">;
  shTrustBar: LpSmbHomeContent["trustBar"]; shServices: LpSmbHomeContent["services"];
  shAbout: LpSmbHomeContent["about"]; shWhyUs: LpSmbHomeContent["whyUs"];
  shProof: LpSmbHomeContent["proof"]; shProcess: LpSmbHomeContent["process"];
  shAreas: LpSmbHomeContent["serviceAreas"]; shFaq: LpSmbHomeContent["faq"];
  shContact: LpSmbHomeContent["contact"]; shFooter: LpSmbHomeContent["footer"];
  shNav: LpSmbHomeContent["nav"];
};
export function splitLpSmbHomeContent(copy: LpSmbHomeContent): LpSmbHomeBlocks {
  const { eyebrow, headline, subhead, primaryCtaText, primaryCtaSub } = copy;
  return {
    shHero: { eyebrow, headline, subhead, primaryCtaText, primaryCtaSub },
    shTrustBar: copy.trustBar, shServices: copy.services, shAbout: copy.about,
    shWhyUs: copy.whyUs, shProof: copy.proof, shProcess: copy.process,
    shAreas: copy.serviceAreas, shFaq: copy.faq, shContact: copy.contact,
    shFooter: copy.footer, shNav: copy.nav,
  };
}
// The prerequisite's canonical snapshot remains available. Editable blocks win
// only when present, including explicitly empty optional values.
export function assembleLpSmbHomeContent(gc: unknown): LpSmbHomeContent {
  const root = object(gc);
  const copy = { ...object(root.smbHomeCopy) };
  for (const key of LP_SMB_HOME_BLOCK_KEYS) {
    if (!Object.hasOwn(root, key)) continue;
    if (key === "shHero") Object.assign(copy, object(root[key]));
    else copy[LP_SMB_HOME_SUBTREES[key][0]] = root[key];
  }
  return normalizeLpSmbHomeContent(copy);
}
export function smbBlockForCopyRoot(root: string): LpSmbHomeBlockKey | null {
  return LP_SMB_HOME_BLOCK_KEYS.find(key =>
    (LP_SMB_HOME_SUBTREES[key] as readonly string[]).includes(root)) ?? null;
}
export const LP_SMB_HOME_IMAGE_SLOTS = [
  { key: "hero", binding: "work-photo-1" },
  { key: "about", binding: "work-photo-2" },
  ...Array.from({ length: 10 }, (_, index) => ({
    key: `work-gallery-${index + 1}`, binding: `work-photo-${index + 3}`,
  })),
] as const;
export const LP_SMB_HOME_ANCHOR_BLOCKS = {
  services: "shServices", about: "shAbout", "why-us": "shWhyUs", proof: "shProof",
  process: "shProcess", areas: "shAreas", faq: "shFaq", contact: "shContact",
} as const satisfies Record<LpSmbHomeNavAnchor, LpSmbHomeBlockKey>;
export function smbBlockHasContent(key: LpSmbHomeBlockKey, value: unknown): boolean {
  const block = object(value);
  if (["shTrustBar", "shServices", "shWhyUs", "shProof", "shProcess", "shFaq", "shNav"].includes(key)) {
    return Array.isArray(block.items) && block.items.some(item =>
      typeof item === "string" ? Boolean(item.trim()) :
      Object.values(object(item)).some(value => typeof value === "string" && value.trim()));
  }
  if (key === "shAreas") return Array.isArray(block.areas) && block.areas.some(Boolean);
  return Object.values(block).some(value => typeof value === "string" && value.trim());
}
export function smbCtaDestination(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const text = value.trim();
  if (text === "#lp-sh-contact") return text;
  if (/^mailto:[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]+@[A-Za-z0-9](?:[A-Za-z0-9.-]*[A-Za-z0-9])?\.[A-Za-z]{2,}$/.test(text)) return text;
  if (/^tel:\+?[0-9][0-9 ()-]{2,30}$/.test(text)) return text;
  if (/^tel:\+?[0-9]{7,20};ext=[0-9]{1,10}$/.test(text)) return text;
  try {
    const url = new URL(text);
    return url.protocol === "https:" && !url.username && !url.password ? text : null;
  } catch { return null; }
}

const SMB_BINDING_FIELDS: Record<string, readonly string[]> = {
  trustBar: ["label"], services: ["heading", "dek"], about: ["heading", "body"],
  whyUs: ["heading"], proof: ["heading"], process: ["heading"],
  serviceAreas: ["heading", "dek"], faq: ["heading"],
  contact: ["heading", "dek", "ctaText", "phone", "email", "address", "hours"],
  footer: ["tagline", "disclaimer"],
};
const SMB_ARRAY_BINDINGS: Record<string, { cap: number; fields?: readonly string[] }> = {
  "trustBar.items": { cap: 6 }, "services.items": { cap: 8, fields: ["icon", "title", "body"] },
  "about.highlights": { cap: 4 }, "whyUs.items": { cap: 6, fields: ["title", "body"] },
  "proof.items": { cap: 6, fields: ["quote", "attribution"] },
  "process.items": { cap: 5, fields: ["title", "body"] },
  "serviceAreas.areas": { cap: 20 }, "faq.items": { cap: 8, fields: ["q", "a"] },
  "nav.items": { cap: 6, fields: ["label", "anchor"] },
};
export function smbCanonicalBindingPath(binding: string): Array<string | number> | null {
  const parts = binding.split(".");
  if (parts.some(part => ["constructor", "prototype", "__proto__"].includes(part))) return null;
  if (parts.length === 1 && (LP_SMB_HOME_SUBTREES.shHero as readonly string[]).includes(binding)) return parts;
  if (parts.length === 2 && Object.hasOwn(SMB_BINDING_FIELDS, parts[0]) && SMB_BINDING_FIELDS[parts[0]].includes(parts[1])) return parts;
  const array = SMB_ARRAY_BINDINGS[parts.slice(0, 2).join(".")];
  if (!array || !/^(0|[1-9][0-9]*)$/.test(parts[2]) || Number(parts[2]) >= array.cap) return null;
  if (array.fields ? parts.length !== 4 || !array.fields.includes(parts[3]) : parts.length !== 3) return null;
  return [parts[0], parts[1], Number(parts[2]), ...parts.slice(3)];
}
export function smbEditableBindingPath(binding: string): Array<string | number> | null {
  // Both frozen canonical markup and editor paths use the same finite grammar.
  const parts = binding.split(".");
  const block = LP_SMB_HOME_BLOCK_KEYS.find(key => key === parts[0]);
  const canonical = block
    ? block === "shHero" ? parts.slice(1).join(".")
      : [LP_SMB_HOME_SUBTREES[block][0], ...parts.slice(1)].join(".")
    : binding;
  const path = smbCanonicalBindingPath(canonical);
  if (!path) return null;
  const root = smbBlockForCopyRoot(String(path[0]))!;
  return root === "shHero" ? [root, ...path] : [root, ...path.slice(1)];
}
